import {isEmpty, some} from 'lodash';
import {useCallback, useContext, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {userSettingsApi} from 'api';
import {ActionButton} from 'components';
import {ROUTE_PATH} from 'constants/routes';
import {UserContext} from 'context';
import {history} from 'context/history';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {ChatGptSettings, InteractiveChatAction, TextChatContentType} from 'types';
import {userAuthUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.SESSION.CHAT_GPT;

type Props = {
    settings: ChatGptSettings;
}

export const AddToLiveHelp: React.FC<Props> = ({settings}) => {
    const {t} = useTranslation();
    const {settings: {featuresAvailability}} = useContext(UserContext);
    const hasLiveHelpPermissions = useMemo(() => userAuthUtils.getUserPermissions()?.content?.clientSession?.liveHelp?.enabled, []);

    const onLiveHelpUpdated = useCallback(() => {
        history.push(ROUTE_PATH.USER.CONTENT.CLIENT_SESSION, {chatGptAdded: true});
    }, []);

    const {
        send: fetchLiveHelpSettings,
        data: liveHelpSettings,
        loading: liveHelpSettingsLoading
    } = useApiData(userSettingsApi.getLiveHelp, {loadingDefault: true});
    const {send: addToLiveHelp} = useApiData(userSettingsApi.addChatGptToLiveHelp, {onSuccess: onLiveHelpUpdated});

    const shouldRender = useMemo(() => {
        if (!hasLiveHelpPermissions || !featuresAvailability.clientSessionLiveHelp || liveHelpSettingsLoading) {
            return false;
        }

        const hasInteractiveChatNodes =
            some(liveHelpSettings?.widget?.welcome?.messages, {contentType: TextChatContentType.InteractiveChat});

        const hasChatGptInteractiveChatNode =
            some(liveHelpSettings?.widget?.interactiveChat, (node) => node.value.action === InteractiveChatAction.ChatGpt);

        return !hasInteractiveChatNodes && !hasChatGptInteractiveChatNode;
    }, [hasLiveHelpPermissions, featuresAvailability, liveHelpSettings, liveHelpSettingsLoading]);

    const isDisabled = useMemo(() =>
        isEmpty(settings?.openAIProperties?.apiKey) || !settings?.openAIProperties?.enabled,
    [settings?.openAIProperties?.apiKey, settings?.openAIProperties?.enabled]);

    const handleAddToLiveHelp = useCallback(() => {
        const labels = {
            chatGPT: t(T_KEYS.LIVE_HELP.LABELS.CHAT_GPT),
            requestAppointment: t(T_KEYS.LIVE_HELP.LABELS.REQUEST_APPOINTMENT),
            callAgent: t(T_KEYS.LIVE_HELP.LABELS.CALL_AGENT)
        };
        addToLiveHelp({labels});
    }, [t, addToLiveHelp]);

    useEffect(() => {
        if (featuresAvailability.clientSessionLiveHelp && hasLiveHelpPermissions) {
            fetchLiveHelpSettings(null);
        }
    }, [featuresAvailability, hasLiveHelpPermissions, fetchLiveHelpSettings]);

    return shouldRender ? (
        <div className='tw-text-right tw-mb-controls'>
            <ActionButton action='create' disabled={isDisabled} icon='logo' onClick={handleAddToLiveHelp}>
                {t(T_KEYS.LIVE_HELP.BUTTON_TEXT)}
            </ActionButton>
        </div>
    ) : null;
};